import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Clipboard } from "@capacitor/clipboard";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LoopIcon from "@mui/icons-material/Loop";

import RevenueCatService from "../services/RevenueCatService";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";

import { Col, Container, Row } from "react-bootstrap";

import LayoutQuiz from "../components/layout/QuizLayouts/layoutQuiz";
import OpenInAppBanner from "../components/layout/OpenInAppBanner";

import {
  D1,
  GradientSpan,
  H1,
  P1,
  P2,
} from "../components/theme/text/Headings";
import { ButtonGradient } from "../components/theme/Button";
import {
  BreakdownBody,
  BreakdownContainer,
  OverallCompatabilityCircle,
  ResultsPageBox,
  UpgradeToProBox,
} from "../components/theme/QuizBox";
import Axis, { LockedAxis } from "../components/theme/axis";
import {
  gradientColors,
  Grey,
  OffWhite,
  White02,
  PageGradient,
} from "../components/theme/colors";
import styled from "styled-components";
import { SecPic, ProPic } from "../components/theme/ProfilePics";
import { auth, db } from "../services/firebase.config";
import { useLoading } from "../services/LoadingContext";
import PersonalityInsights from "../components/PersonalityInsights";
import {
  CompatibilityInsights,
  PersonalInsights,
} from "../components/SharedInsights";
import { calculateKinseyResult, getQuizSummary } from "../utils/derivedFields";

// Styled components for feedback section
const FeedbackContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
`;

const FeedbackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 10px 16px;
  color: ${Grey};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Generic Results Template Component
 *
 * This template handles all common results page logic including:
 * - User authentication and profile fetching
 * - Shared quiz data handling
 * - Percentage calculation and compatibility scoring
 * - Dynamic UI rendering based on quiz configuration
 *
 * @param {Object} quizConfig - Configuration object from quizConfigs.js
 */
const ResultsTemplate = ({ quizConfig }) => {
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [username, setUsername] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [quizId, setQuizId] = useState("");

  const [quizId2, setQuizId2] = useState("");
  const [displayName2, setDisplayName2] = useState("");
  const [photoURL2, setPhotoURL2] = useState("");
  const [username2, setUsername2] = useState("");

  const [searchParams] = useSearchParams();
  const sharedId = searchParams.get("shared");

  const [percentages, setPercentages] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizAnswers2, setQuizAnswers2] = useState({});
  const [regenerateTimestamp, setRegenerateTimestamp] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // Pro status state
  const [isProUser, setIsProUser] = useState(false);

  // Feedback state
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // User role state for shared quizzes
  const [isCurrentUserSender, setIsCurrentUserSender] = useState(null);

  const handlePercentageUpdate = (field, percentage1, percentage2) => {
    setPercentages((prev) => [
      ...prev.filter((item) => item.field !== field),
      { field, percentage1, percentage2 },
    ]);
  };

  // Dynamic trait determination based on quiz configuration
  const determineTrait = (field, percentage, allScores = null) => {
    if (
      !quizConfig.results.showTraitSummary ||
      !quizConfig.results.traitMapping
    ) {
      return null;
    }

    const mapping = quizConfig.results.traitMapping[field];
    if (!mapping) return null;

    // Handle standard high/low mappings (like Myers-Briggs)
    return percentage > 50 ? mapping.high : mapping.low;
  };

  /**
   * Get summary label for the quiz (e.g., "ENFP", "Liberal", "Quality Time")
   * Uses summaryConfig from quiz configuration (local or remote)
   * @param {Object} scores - Quiz scores by field
   * @returns {string|null} The summary label or null
   */
  const getSummaryLabelForUser = (scores) => {
    // Use dynamic summaryConfig from quiz configuration
    return getQuizSummary(quizConfig, scores, percentages);
  };

  /**
   * Get summary label for second user in shared quiz
   * Extracts scores from percentage2 values in the percentages array
   * @returns {string|null} The summary label or null
   */
  const getSummaryLabelForSecondUser = () => {
    if (!quizId2 || percentages.length === 0) return null;

    // Build scores object from percentage2 values
    const scores = {};
    percentages.forEach((item) => {
      if (item.percentage2 !== undefined && item.percentage2 !== null) {
        scores[item.field] = item.percentage2;
      }
    });

    if (Object.keys(scores).length === 0) return null;

    return getSummaryLabelForUser(scores);
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) setLoading(false);
    });

    return () => unsubscribe();
  }, [setLoading]);

  // Load quiz answers for calculations (attachment, political, kinsey, love-languages)
  useEffect(() => {
    const loadQuizAnswers = async () => {
      if (
        !currentUser ||
        !quizId ||
        (quizConfig.quizType !== "attachment-styles" &&
          quizConfig.quizType !== "political-alignment" &&
          quizConfig.quizType !== "am-i-gay" &&
          quizConfig.quizType !== "love-languages")
      )
        return;

      try {
        const docRef = doc(db, "quiz-answers", quizId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Process answers to get average scores per field
          const fieldScores = {};
          Object.values(data.answers).forEach((answer) => {
            if (answer.field && typeof answer.percentage === "number") {
              if (!fieldScores[answer.field]) {
                fieldScores[answer.field] = [];
              }
              fieldScores[answer.field].push(answer.percentage);
            }
          });

          // Calculate average for each field
          const processedScores = {};
          Object.entries(fieldScores).forEach(([field, percentages]) => {
            const average =
              percentages.reduce((sum, percent) => sum + percent, 0) /
              percentages.length;
            processedScores[field] = Math.round(average);
          });

          setQuizAnswers(processedScores);
        }
      } catch (error) {
        console.error("Error loading quiz answers:", error);
      }
    };

    loadQuizAnswers();
  }, [currentUser, quizId, quizConfig.quizType]);

  // Load second user's quiz answers for Kinsey when shared
  useEffect(() => {
    const loadSecondQuizAnswers = async () => {
      if (!quizId2 || quizConfig.quizType !== "am-i-gay") return;

      try {
        const docRef = doc(db, "quiz-answers", quizId2);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const fieldScores = {};
          Object.values(data.answers).forEach((answer) => {
            if (answer.field && typeof answer.percentage === "number") {
              if (!fieldScores[answer.field]) fieldScores[answer.field] = [];
              fieldScores[answer.field].push(answer.percentage);
            }
          });
          const processedScores = {};
          Object.entries(fieldScores).forEach(([field, percentages]) => {
            const average =
              percentages.reduce((sum, percent) => sum + percent, 0) /
              percentages.length;
            processedScores[field] = Math.round(average);
          });
          setQuizAnswers2(processedScores);
        }
      } catch (e) {
        // ignore
      }
    };

    loadSecondQuizAnswers();
  }, [quizId2, quizConfig.quizType]);

  // Check Pro status when user changes
  useEffect(() => {
    const checkProStatus = async () => {
      if (!currentUser) {
        setIsProUser(false);
        return;
      }

      try {
        const customerInfo = await RevenueCatService.getCustomerInfo();
        const isPro = RevenueCatService.isProFromCustomerInfo(customerInfo);
        setIsProUser(isPro);
      } catch (error) {
        setIsProUser(false);
      }
    };

    checkProStatus();
  }, [currentUser]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUser) return;

      const profileDocRef = doc(db, "public-profiles", currentUser.uid);
      const profileDoc = await getDoc(profileDocRef);

      if (profileDoc.exists()) {
        const data = profileDoc.data();
        setUsername(data.username || "");
        setPhotoURL(data.photoURL || "");
        setDisplayName(data.displayName || "");

        // Use the dynamic database field from config
        setQuizId(data[quizConfig.databaseField]);
      }
    };

    if (currentUser) fetchProfileData();
  }, [currentUser, quizConfig.databaseField]);

  useEffect(() => {
    const getSharedCollection = async () => {
      if (!sharedId || !currentUser) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        if (sharedId) {
          const sharedDocRef = doc(db, "shared-quizzes", sharedId);
          const sharedDoc = await getDoc(sharedDocRef);

          if (sharedDoc.data().senderUid === currentUser.uid) {
            // Current user is the sender
            setIsCurrentUserSender(true);
            setQuizId2(sharedDoc.data().receiverQuizId);

            const profile2DocRef = doc(
              db,
              "public-profiles",
              sharedDoc.data().receiverUid
            );
            const profile2Doc = await getDoc(profile2DocRef);

            if (profile2Doc.exists()) {
              const data = profile2Doc.data();
              setUsername2(data.username || "");
              setPhotoURL2(data.photoURL || "");
              setDisplayName2(data.displayName || "");
            }
          } else if (sharedDoc.data().senderUid !== currentUser.uid) {
            // Current user is the receiver
            setIsCurrentUserSender(false);
            setQuizId2(sharedDoc.data().senderQuizId);

            const profile2DocRef = doc(
              db,
              "public-profiles",
              sharedDoc.data().senderUid
            );
            const profile2Doc = await getDoc(profile2DocRef);

            if (profile2Doc.exists()) {
              const data = profile2Doc.data();
              setUsername2(data.username || "");
              setPhotoURL2(data.photoURL || "");
              setDisplayName2(data.displayName || "");
            }
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    getSharedCollection();
  }, [sharedId, currentUser, quizId2, setLoading]);

  // Track if we've logged this calculation already
  const loggedCompatibilityRef = useRef(null);

  const calculateOverallCompatibility = () => {
    if (percentages.length === 0) return null;

    const debugData = [];

    const similarities = percentages.map((item) => {
      const { percentage1, percentage2, field } = item;
      if (
        percentage1 === undefined ||
        percentage2 === undefined ||
        isNaN(percentage1) ||
        isNaN(percentage2)
      )
        return 0;

      const diff = Math.abs(percentage1 - percentage2);

      // Check if this axis uses reverse compatibility (opposites attract)
      const axisConfig = quizConfig.results?.axes?.find(
        (a) => a.field === field
      );
      const useReverse = axisConfig?.reverseCompatibility === true;
      const result = useReverse ? diff : 100 - diff;

      debugData.push({
        field,
        percentage1,
        percentage2,
        diff,
        useReverse,
        result,
      });

      // For reverse compatibility, further apart = more compatible
      return result;
    });

    const sum = similarities.reduce((acc, val) => acc + val, 0);
    const average = sum / similarities.length;
    const final = Math.round(average);

    // Only log once per unique calculation
    const logKey = JSON.stringify(percentages);
    if (loggedCompatibilityRef.current !== logKey && percentages.length > 0) {
      loggedCompatibilityRef.current = logKey;
      console.log("📊 === COMPATIBILITY CALCULATION ===");
      debugData.forEach(
        ({ field, percentage1, percentage2, diff, useReverse, result }) => {
          console.log(
            `📈 ${field}: You=${percentage1} | Them=${percentage2} | Gap=${diff} | ${
              useReverse ? "REVERSE" : "normal"
            } → ${result}%`
          );
        }
      );
      console.log(
        `📊 TOTAL: ${sum} / ${similarities.length} axes = ${average.toFixed(
          1
        )} → ${final}%`
      );
      console.log("📊 ===================================");
    }

    return final;
  };

  // Generate share URL (matching Home page logic)
  const shareURL = quizId
    ? `/s/${quizConfig.quizType}/${encodeURIComponent(
        displayName || "Someone"
      )}?from=${quizId}`
    : null;

  const copyToClipboard = useCallback(
    async (URL) => {
      try {
        await Clipboard.write({ string: URL });
        alert("Link copied to clipboard!");

        // Track quiz link copied from results page
        const linkCopiedProps = {
          quiz_type: quizConfig.quizType,
          quiz_name: quizConfig.title,
          source: "results",
          url: URL,
          user_id: currentUser?.uid || "anonymous",
          has_shared_quiz: !!sharedId,
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track("Quiz Link Copied", linkCopiedProps);
        postHogService.track("Quiz Link Copied", linkCopiedProps);
      } catch (err) {
        // Silently fail - clipboard error already handled with user feedback
      }
    },
    [quizConfig.quizType, quizConfig.title, currentUser, sharedId]
  );

  const handleRegenerateAll = useCallback(() => {
    // Check if user is Pro - if not, navigate to Pro page
    if (!isProUser) {
      // Track paywall hit
      const paywallHitProps = {
        feature: "results_regenerate",
        location: "results_page",
        quiz_type: quizConfig.quizType,
        quiz_id: quizId,
        user_id: currentUser?.uid,
        shared_quiz: !!sharedId,
        shared_id: sharedId,
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Paywall Hit - Regenerate", paywallHitProps);
      postHogService.track("Paywall Hit - Regenerate", paywallHitProps);

      navigate("/pro");
      return;
    }

    // Track Pro user regeneration
    const proRegenerateProps = {
      feature: "results_regenerate",
      location: "results_page",
      quiz_type: quizConfig.quizType,
      quiz_id: quizId,
      user_id: currentUser?.uid,
      is_pro: true,
      shared_quiz: !!sharedId,
      shared_id: sharedId,
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Pro Feature Used - Regenerate", proRegenerateProps);
    postHogService.track("Pro Feature Used - Regenerate", proRegenerateProps);

    setRegenerating(true);
    setRegenerateTimestamp(Date.now()); // Use timestamp to trigger regeneration

    // Track regenerate as "not helpful" feedback
    const regenerateFeedbackProps = {
      feedback_type: "not_helpful_regenerate",
      quiz_type: quizConfig.quizType,
      quiz_id: quizId,
      user_id: currentUser?.uid,
      shared_quiz: !!sharedId,
      shared_id: sharedId,
      insight_types_shown: [
        // Check what insights are currently shown
        percentages.length > 0 ? "personality_insights" : null,
        sharedId ? "compatibility_insights" : null,
        sharedId && isProUser ? "personal_insights" : null,
      ].filter(Boolean),
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Insight Feedback", regenerateFeedbackProps);
    postHogService.track("Insight Feedback", regenerateFeedbackProps);

    // Reset regenerating state after a delay to allow components to process
    setTimeout(() => {
      setRegenerating(false);
    }, 3000); // Increased timeout to allow for regeneration
  }, [
    quizConfig.quizType,
    quizId,
    currentUser?.uid,
    sharedId,
    percentages.length,
    isProUser,
    navigate,
  ]);

  const handleHelpfulClick = useCallback(() => {
    if (feedbackSubmitted) return; // Prevent multiple submissions

    // Track helpful feedback in Mixpanel and PostHog
    const helpfulFeedbackProps = {
      feedback_type: "helpful",
      quiz_type: quizConfig.quizType,
      quiz_id: quizId,
      user_id: currentUser?.uid,
      shared_quiz: !!sharedId,
      shared_id: sharedId,
      insight_types_shown: [
        // Check what insights are currently shown
        percentages.length > 0 ? "personality_insights" : null,
        sharedId ? "compatibility_insights" : null,
        sharedId && isProUser ? "personal_insights" : null,
      ].filter(Boolean),
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Insight Feedback", helpfulFeedbackProps);
    postHogService.track("Insight Feedback", helpfulFeedbackProps);

    // Provide user feedback
    setFeedbackSubmitted(true);
  }, [
    feedbackSubmitted,
    quizConfig.quizType,
    quizId,
    currentUser?.uid,
    sharedId,
    percentages.length,
    isProUser,
  ]);

  // Get colors from the quiz configuration with fallback to Firestore config
  const getGradientConfig = () => {
    // Try static gradient colors first
    if (gradientColors[quizConfig.quizType]) {
      return gradientColors[quizConfig.quizType];
    }

    // Fall back to config's gradientColors (from Firestore)
    if (quizConfig.gradientColors) {
      return quizConfig.gradientColors;
    }

    // Default fallback
    return {
      background: "#3B96FF",
      transitionBackground: "#F951FF",
    };
  };

  const colors = getGradientConfig();

  return (
    <>
      <OpenInAppBanner />
      <LayoutQuiz $background={PageGradient}>
        <Container>
          <Row>
            <Col
              lg={{ offset: 4, span: 4 }}
              md={{ offset: 3, span: 6 }}
              sm={{ offset: 2, span: 8 }}
              xs={{ span: 12 }}
              className="text-align-center"
            >
              <ResultsPageBox style={{ flexDirection: "column" }}>
                <D1 className="mb-30 mt-0">
                  Your{" "}
                  <GradientSpan
                    $background={colors.background}
                    $transitionBackground={colors.transitionBackground}
                  >
                    {quizConfig.results.titleText}
                  </GradientSpan>{" "}
                  Vibe Check results are...
                </D1>

                {!sharedId ? (
                  <div
                    className="display-flex display-column"
                    style={{ gap: "4px" }}
                  >
                    {photoURL ? (
                      <ProPic
                        src={photoURL}
                        $borderColor={colors.background}
                        style={{ cursor: "pointer" }}
                        onClick={() => username && navigate(`/${username}`)}
                      />
                    ) : (
                      <SecPic
                        $borderColor={colors.background}
                        style={{ cursor: "pointer" }}
                        onClick={() => username && navigate(`/${username}`)}
                      >
                        <D1>{displayName ? displayName[0] : "?"}</D1>
                      </SecPic>
                    )}
                    <P2 color={Grey} style={{ margin: 0 }}>
                      {username
                        ? `@${username}`
                        : displayName
                        ? displayName
                        : "Anonymous User"}
                    </P2>
                    {/* Conditionally render trait summary for Myers-Briggs and Attachment Styles */}
                    {quizConfig.results.showTraitSummary && (
                      <div
                        className="display-flex"
                        style={{
                          gap:
                            quizConfig.quizType === "am-i-gay" ? "2px" : "5px",
                          flexDirection:
                            quizConfig.quizType === "am-i-gay"
                              ? "column"
                              : "row",
                        }}
                      >
                        {/* Special case for Kinsey (Am I Gay?) */}
                        {quizConfig.quizType === "am-i-gay" &&
                          Object.keys(quizAnswers).length > 0 &&
                          (() => {
                            const { number, label } =
                              calculateKinseyResult(quizAnswers);
                            return (
                              <>
                                <P1
                                  key="kinseySelf"
                                  className="mb-0"
                                >{`Kinsey ${number}`}</P1>
                                <P2 key="kinseySelfLabel" className="mt-0">
                                  {label}
                                </P2>
                              </>
                            );
                          })()}
                        {/* Show summary label if available, otherwise show individual traits */}
                        {getSummaryLabelForUser(quizAnswers) ? (
                          <P1 key="summaryLabel">
                            {getSummaryLabelForUser(quizAnswers)}
                          </P1>
                        ) : (
                          percentages.map((item) => {
                            const trait = determineTrait(
                              item.field,
                              item.percentage1
                            );
                            return trait ? (
                              <P1 key={item.field}>{trait}</P1>
                            ) : null;
                          })
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="display-flex"
                    style={{ alignItems: "flex-start", width: "100%" }}
                  >
                    <div
                      className="display-flex display-column"
                      style={{ gap: "2px", flex: 1, minWidth: 0 }}
                    >
                      {photoURL ? (
                        <ProPic
                          src={photoURL}
                          $borderColor={colors.background}
                          style={{ cursor: "pointer" }}
                          onClick={() => username && navigate(`/${username}`)}
                        />
                      ) : (
                        <SecPic
                          $borderColor={colors.background}
                          style={{ cursor: "pointer" }}
                          onClick={() => username && navigate(`/${username}`)}
                        >
                          <D1>{displayName ? displayName[0] : "?"}</D1>
                        </SecPic>
                      )}
                      <P2 color={Grey} style={{ margin: 0 }}>
                        {username
                          ? `@${username}`
                          : displayName
                          ? displayName
                          : "Anonymous User"}
                      </P2>
                      {/* Conditionally render trait summary for first user */}
                      {quizConfig.results.showTraitSummary && (
                        <div
                          className="display-flex"
                          style={{
                            gap:
                              quizConfig.quizType === "am-i-gay"
                                ? "2px"
                                : "5px",
                            flexDirection:
                              quizConfig.quizType === "am-i-gay"
                                ? "column"
                                : "row",
                          }}
                        >
                          {/* Special case for Kinsey (Am I Gay?) */}
                          {quizConfig.quizType === "am-i-gay" &&
                            Object.keys(quizAnswers).length > 0 &&
                            (() => {
                              const { number, label } =
                                calculateKinseyResult(quizAnswers);
                              return (
                                <>
                                  <P1
                                    key="kinseySelfSharedNumber"
                                    className="mb-0"
                                  >{`Kinsey ${number}`}</P1>
                                  <P2
                                    key="kinseySelfSharedLabel"
                                    className="mt-0"
                                  >
                                    {label}
                                  </P2>
                                </>
                              );
                            })()}
                          {/* Show summary label if available, otherwise show individual traits */}
                          {getSummaryLabelForUser(quizAnswers) ? (
                            <P1 key="summaryLabel">
                              {getSummaryLabelForUser(quizAnswers)}
                            </P1>
                          ) : (
                            percentages.map((item) => {
                              const trait = determineTrait(
                                item.field,
                                item.percentage1
                              );
                              return trait ? (
                                <P1 key={item.field}>{trait}</P1>
                              ) : null;
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <OverallCompatabilityCircle
                      percent={calculateOverallCompatibility()}
                      gradientColors={{
                        color1: colors.background,
                        color2: colors.transitionBackground,
                      }}
                      bgColor={White02}
                    >
                      <D1 color={OffWhite} className="mb-0">
                        {calculateOverallCompatibility()}%
                      </D1>
                      <P2 className="mt-0" style={{ fontWeight: "900" }}>
                        Match
                      </P2>
                    </OverallCompatabilityCircle>
                    <div
                      className="display-flex display-column"
                      style={{ gap: "2px", flex: 1, minWidth: 0 }}
                    >
                      {photoURL2 ? (
                        <ProPic
                          src={photoURL2}
                          $borderColor={colors.transitionBackground}
                          style={{ cursor: "pointer" }}
                          onClick={() => username2 && navigate(`/${username2}`)}
                        />
                      ) : (
                        <SecPic
                          $borderColor={colors.transitionBackground}
                          style={{ cursor: "pointer" }}
                          onClick={() => username2 && navigate(`/${username2}`)}
                        >
                          <D1>{displayName2 ? displayName2[0] : "?"}</D1>
                        </SecPic>
                      )}
                      <P2 color={Grey} style={{ margin: 0 }}>
                        {username2
                          ? `@${username2}`
                          : displayName2
                          ? displayName2
                          : "Anonymous User"}
                      </P2>
                      {/* Conditionally render trait summary for second user */}
                      {quizConfig.results.showTraitSummary && (
                        <div
                          className="display-flex"
                          style={{
                            gap:
                              quizConfig.quizType === "am-i-gay"
                                ? "2px"
                                : "5px",
                            flexDirection:
                              quizConfig.quizType === "am-i-gay"
                                ? "column"
                                : "row",
                          }}
                        >
                          {/* Special case for Kinsey (Am I Gay?) - second user */}
                          {quizConfig.quizType === "am-i-gay" &&
                            Object.keys(quizAnswers2).length > 0 &&
                            (() => {
                              const { number, label } =
                                calculateKinseyResult(quizAnswers2);
                              return (
                                <>
                                  <P1
                                    key="kinseyOtherNumber"
                                    className="mb-0"
                                  >{`Kinsey ${number}`}</P1>
                                  <P2 key="kinseyOtherLabel" className="mt-0">
                                    {label}
                                  </P2>
                                </>
                              );
                            })()}
                          {/* Show summary label if available, otherwise show individual traits */}
                          {getSummaryLabelForSecondUser() ? (
                            <P1 key="summaryLabel2">
                              {getSummaryLabelForSecondUser()}
                            </P1>
                          ) : (
                            percentages.map((item) => {
                              const trait = determineTrait(
                                item.field,
                                item.percentage2
                              );
                              return trait ? (
                                <P1 key={item.field}>{trait}</P1>
                              ) : null;
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </ResultsPageBox>
            </Col>
          </Row>
          {!sharedId && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <ButtonGradient
                  $background={colors.background}
                  $transitionBackground={colors.transitionBackground}
                  width="90%"
                  className="mb-20 mt-10"
                  style={{ marginRight: 0 }}
                  onClick={() =>
                    shareURL &&
                    copyToClipboard(`https://www.vibecheckme.com${shareURL}`)
                  }
                  disabled={!shareURL}
                >
                  Send this Quiz 🎉
                </ButtonGradient>
              </Col>
            </Row>
          )}
          {/* AI Personality Insights Section - Only show for individual results (not shared) */}
          {!sharedId && percentages.length > 0 && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <PersonalityInsights
                  quizResults={percentages}
                  quizType={quizConfig.quizType}
                  colors={colors}
                  quizTitle={quizConfig.results.titleText}
                  quizId={quizId}
                  forceRegenerate={regenerateTimestamp}
                />
              </Col>
            </Row>
          )}
          {/* Compare your Answers Section - Pro users only */}
          {sharedId && isProUser && (
            <Row className="mt-10 mb-15">
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <ButtonGradient
                  width="85%"
                  $background={colors.background}
                  $transitionBackground={colors.transitionBackground}
                  onClick={() => navigate(`/shared-answers/${sharedId}`)}
                >
                  Compare your Answers 👀
                </ButtonGradient>
              </Col>
            </Row>
          )}

          {/* AI Compatibility Insights Section - Only show for shared results, above x-axis */}
          {sharedId && percentages.length > 0 && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <CompatibilityInsights
                  quizResults={percentages}
                  quizType={quizConfig.quizType}
                  colors={colors}
                  displayName={displayName}
                  displayName2={displayName2}
                  sharedId={sharedId}
                  forceRegenerate={regenerateTimestamp}
                  isCurrentUserSender={isCurrentUserSender}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col
              lg={{ offset: 4, span: 4 }}
              md={{ offset: 3, span: 6 }}
              sm={{ offset: 2, span: 8 }}
              xs={{ span: 12 }}
              className="text-align-center"
            >
              <BreakdownContainer>
                <BreakdownBody>
                  {/* Dynamically render axes with Pro gating */}
                  {quizConfig.results.axes.map((axis) =>
                    axis.locked && !isProUser ? (
                      <LockedAxis
                        key={axis.field}
                        leftLabel={axis.leftLabel}
                        rightLabel={axis.rightLabel}
                        leftDescription={axis.leftDescription}
                        rightDescription={axis.rightDescription}
                        colorOne={colors.background}
                        colorTwo={colors.transitionBackground}
                        onClick={() => navigate("/pro")}
                      />
                    ) : (
                      <Axis
                        key={axis.field}
                        quizName={quizConfig.quizName}
                        quizId={quizId}
                        senderQuizId={quizId2}
                        leftLabel={axis.leftLabel}
                        rightLabel={axis.rightLabel}
                        leftDescription={axis.leftDescription}
                        rightDescription={axis.rightDescription}
                        displayName={displayName}
                        profilePicture={photoURL}
                        profilePicture2={photoURL2}
                        displayName2={displayName2}
                        field={axis.field}
                        onPercentageUpdate={handlePercentageUpdate}
                        colorOne={colors.background}
                        colorTwo={colors.transitionBackground}
                      />
                    )
                  )}
                </BreakdownBody>
              </BreakdownContainer>
            </Col>
          </Row>

          {sharedId && !isProUser && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <BreakdownContainer>
                  <UpgradeToProBox>
                    <H1>See how they answered each question 👀</H1>
                    <ButtonGradient
                      width="95%"
                      className="mt-20"
                      $background={colors.background}
                      $transitionBackground={colors.transitionBackground}
                      onClick={() => navigate("/pro")}
                    >
                      Upgrade to Pro
                    </ButtonGradient>
                  </UpgradeToProBox>
                </BreakdownContainer>
              </Col>
            </Row>
          )}

          {/* AI Personal Insights Section - Only for shared results and Pro users */}
          {sharedId && percentages.length > 0 && isProUser && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <PersonalInsights
                  quizResults={percentages}
                  quizType={quizConfig.quizType}
                  colors={colors}
                  displayName={displayName}
                  displayName2={displayName2}
                  sharedId={sharedId}
                  forceRegenerate={regenerateTimestamp}
                  isCurrentUserSender={isCurrentUserSender}
                />
              </Col>
            </Row>
          )}

          {/* Not a Pro User, show it blurred out for shared results */}
          {sharedId && !isProUser && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <PersonalInsights
                  quizResults={percentages}
                  quizType={quizConfig.quizType}
                  colors={colors}
                  displayName={displayName}
                  displayName2={displayName2}
                  sharedId={sharedId}
                  forceRegenerate={regenerateTimestamp}
                  isCurrentUserSender={isCurrentUserSender}
                  isLocked={true}
                  onUnlockClick={() => navigate("/pro")}
                />
              </Col>
            </Row>
          )}
          {/* AI Feedback Section - Only show if there are any insights */}
          {percentages.length > 0 && (
            <Row>
              <Col
                lg={{ offset: 4, span: 4 }}
                md={{ offset: 3, span: 6 }}
                sm={{ offset: 2, span: 8 }}
                xs={{ span: 12 }}
                className="text-align-center"
              >
                <FeedbackContainer>
                  <FeedbackButton
                    onClick={handleHelpfulClick}
                    disabled={regenerating || feedbackSubmitted}
                  >
                    <ThumbUpIcon style={{ fontSize: 17 }} />
                    {feedbackSubmitted ? "Thanks!" : "Helpful"}
                  </FeedbackButton>
                  <FeedbackButton
                    onClick={handleRegenerateAll}
                    disabled={regenerating}
                  >
                    {regenerating ? (
                      "Regenerating..."
                    ) : (
                      <>
                        <LoopIcon style={{ fontSize: 17 }} /> Regenerate
                      </>
                    )}
                  </FeedbackButton>
                </FeedbackContainer>
              </Col>
            </Row>
          )}
        </Container>
      </LayoutQuiz>
    </>
  );
};

export default ResultsTemplate;
